import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSettingsBillingQueryKey,
  useGetSettingsBilling,
  usePutSettingsBilling,
} from '@agos/api-client/platform'
import { T, formatCents, formatCount, tt, useAuth } from '@agos/shared'
import { PageHeader, Spinner, cn, useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Building2, Check, FileText, Landmark, Save, ShieldCheck } from 'lucide-react'
import { useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import {
  DUE_DAYS_MAX,
  DUE_DAYS_MIN,
  approvalMessage,
  approvalState,
  formOf,
  icDphProblem,
  identityLabel,
  identityProblem,
  identityProblemMessage,
  missingIdentity,
  payloadOf,
  problemsOf,
  suggestedIcDph,
  thresholdCents,
  type BillingForm,
  type BillingSettings,
  type IdentityField,
} from './billing-settings'

/**
 * `/settings-billing` — our own identity on the invoices we issue, and the approval gate.
 *
 * **FB-135.** `approvalThresholdEur` is a switch dressed as a limit. `InvoiceService` filters
 * `signum() > 0` and the invoice list's `needsApproval` returns false for any invoice when the threshold
 * is zero — so with the field unset, **nothing ever needs a main administrator's approval**, including
 * the two invoices over 500 € in the restored data. Legacy shows a number field defaulting to 0 and says
 * nothing about the control it is turning off on another page.
 *
 * **FB-136.** The four identifiers printed on every outgoing invoice — IČO, DIČ, IČ DPH, IBAN — are
 * unvalidated plain text here, while the *supplier's* four are checked on `/invoices` with helpers that
 * already live in `@agos/shared`. The same four fields, one screen apart, one side checked.
 *
 * **And the section is replaced, not merged**, so the payload carries all eleven keys — `logoUrl`
 * included, because `GET /settings/footer` puts it on the customer site.
 *
 * Ported from legacy `pages/settings-billing.tsx` (180 lines).
 */

export function SettingsBillingPage() {
  const { data, isLoading } = useGetSettingsBilling()

  if (isLoading || data === undefined) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return <BillingEditor settings={data} />
}

const FIELD =
  'px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 w-full disabled:opacity-60'

function BillingEditor({ settings }: { settings: BillingSettings }) {
  const { toast } = useToast()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const [form, setForm] = useState<BillingForm>(() => formOf(settings))
  const writable = mayWrite('billing', user?.systemRole)
  const readOnly = readOnlyReason('billing', user?.systemRole)

  const problems = problemsOf(form)
  const missing = missingIdentity(form)
  const approval = approvalState(form)
  const suggestion = suggestedIcDph(form)

  const save = usePutSettingsBilling({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetSettingsBillingQueryKey() })
        toast({ title: tt('Fakturačné údaje uložené') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Uloženie zlyhalo'),
          description: apiErrorMessage(caught, tt('Na serveri sa nič nezmenilo.')),
        }),
    },
  })

  const set = <K extends keyof BillingForm>(key: K, value: BillingForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Fakturačné údaje')}
        description={tt('Čo je na faktúrach, ktoré vydávame, a kedy treba schválenie')}
        icon={<FileText className="w-5 h-5" />}
      />

      {readOnly !== null && (
        <p className="text-[11px] text-amber-500" data-read-only>
          {readOnly}
        </p>
      )}

      {/* FB-135. The zero is not neutral - it turns the approval gate off on `/invoices`. */}
      <div
        className={cn(
          'rounded-2xl border px-4 py-3 flex items-start gap-3',
          approval === 'on' ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-amber-500/40 bg-amber-500/5',
        )}
        data-approval={approval}
      >
        {approval === 'on' ? (
          <ShieldCheck className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        )}
        <div className="min-w-0">
          <p className={cn('text-sm font-semibold', approval === 'on' ? 'text-emerald-500' : 'text-amber-500')}>
            {approval === 'on' ? (
              <>
                <T>Schválenie sa vyžaduje nad</T> {formatCents(thresholdCents(form.approvalThreshold))}
              </>
            ) : (
              <T>Schvaľovanie faktúr je vypnuté</T>
            )}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{approvalMessage(form)}</p>
        </div>
      </div>

      {missing.length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-xs" data-missing-identity>
          <p className="font-bold text-amber-500 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            {formatCount(missing.length)} <T>z fakturačných identifikátorov chýba</T>
          </p>
          <p className="text-muted-foreground mt-0.5">
            {missing.map((field) => identityLabel(field)).join(', ')} -{' '}
            <T>
              faktúra vydaná bez nich je doklad, ktorý zákazník nemá z čoho zaplatiť a účtovník ho pošle
              späť.
            </T>
          </p>
        </div>
      )}

      <Section title={tt('Firma')} icon={<Building2 className="w-4 h-4 text-primary" />}>
        {(['ico', 'dic', 'icDph'] as const).map((field) => (
          <IdentityInput
            key={field}
            field={field}
            value={form[field]}
            problem={field === 'icDph' ? icDphProblem(form) : identityProblem(field, form[field])}
            disabled={!writable}
            onChange={(value) => set(field, value)}
            suggestion={field === 'icDph' ? suggestion : null}
            onAccept={field === 'icDph' && suggestion !== null ? () => set('icDph', suggestion) : undefined}
          />
        ))}
        <Field label={tt('Fakturačná adresa')} htmlFor="billing-address">
          <input
            id="billing-address"
            className={FIELD}
            disabled={!writable}
            value={form.billingAddress}
            onChange={(event) => set('billingAddress', event.target.value)}
          />
        </Field>
      </Section>

      <Section title={tt('Platby')} icon={<Landmark className="w-4 h-4 text-primary" />}>
        <IdentityInput
          field="iban"
          value={form.iban}
          problem={identityProblem('iban', form.iban)}
          disabled={!writable}
          onChange={(value) => set('iban', value)}
          suggestion={null}
        />
        <Field label={tt('Splatnosť faktúry (dni)')} htmlFor="billing-due">
          <input
            id="billing-due"
            type="number"
            min={DUE_DAYS_MIN}
            max={DUE_DAYS_MAX}
            className={FIELD}
            disabled={!writable}
            value={form.invoiceDueDays}
            onChange={(event) => set('invoiceDueDays', Number(event.target.value))}
          />
        </Field>
        <Field label={tt('Schválenie faktúry nad (€)')} htmlFor="billing-threshold">
          <input
            id="billing-threshold"
            className={FIELD}
            disabled={!writable}
            placeholder={tt('napríklad 500 - prázdne znamená bez schvaľovania')}
            value={form.approvalThreshold}
            onChange={(event) => set('approvalThreshold', event.target.value)}
          />
          <p className="text-[11px] text-muted-foreground" data-threshold-note>
            {/* Legacy shows a bare `0` here, which reads as *no limit* and means *no gate*. */}
            {approval === 'off' ? (
              <T>Prázdne alebo nula znamená, že schválenie nepotrebuje žiadna faktúra.</T>
            ) : (
              <T>Faktúry nad túto sumu potrebujú schválenie hlavného administrátora.</T>
            )}
          </p>
        </Field>
      </Section>

      <Section title={tt('Vzhľad faktúry')} icon={<FileText className="w-4 h-4 text-primary" />}>
        <Field label={tt('Logo na faktúre a v pätičke webu')} htmlFor="billing-logo">
          <input
            id="billing-logo"
            className={FIELD}
            disabled={!writable}
            value={form.logoUrl}
            onChange={(event) => set('logoUrl', event.target.value)}
          />
          <p className="text-[11px] text-muted-foreground">
            {/* One field, two places - `GET /settings/footer` reads this one. */}
            <T>Toto logo používa aj pätička zákazníckeho webu.</T>
          </p>
        </Field>
        <Field label={tt('Poznámka na faktúre')} htmlFor="billing-note">
          <input
            id="billing-note"
            className={FIELD}
            disabled={!writable}
            value={form.invoiceNote}
            onChange={(event) => set('invoiceNote', event.target.value)}
          />
        </Field>
        <Toggle
          label={tt('Automatická faktúra')}
          description={tt('Vystaví faktúru bez zásahu obsluhy.')}
          value={form.autoInvoice}
          disabled={!writable}
          onToggle={() => set('autoInvoice', !form.autoInvoice)}
          testId="autoInvoice"
        />
        <Toggle
          label={tt('Poslať faktúru e-mailom')}
          description={tt('Odošle vystavenú faktúru zákazníkovi.')}
          value={form.sendInvoiceEmail}
          disabled={!writable}
          onToggle={() => set('sendInvoiceEmail', !form.sendInvoiceEmail)}
          testId="sendInvoiceEmail"
        />
      </Section>

      <div className="flex items-center gap-3">
        <button
          type="button"
          data-save
          disabled={!writable || save.isPending || problems.blocking}
          onClick={() => save.mutate({ data: { ...payloadOf(form) } })}
          className={cn('flex items-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
        >
          {save.isPending ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť</T>
        </button>
        {problems.blocking && (
          <span className="text-[11px] text-red-500" data-save-blocked>
            {formatCount(problems.fields.length)}{' '}
            <T>z identifikátorov nie je platný - opravte ich pred uložením.</T>
          </span>
        )}
      </div>
    </div>
  )
}

function IdentityInput({
  field,
  value,
  problem,
  disabled,
  onChange,
  suggestion,
  onAccept,
}: {
  field: IdentityField
  value: string
  problem: ReturnType<typeof identityProblem>
  disabled: boolean
  onChange: (value: string) => void
  suggestion: string | null
  onAccept?: () => void
}) {
  return (
    <Field label={identityLabel(field)} htmlFor={`billing-${field}`}>
      <input
        id={`billing-${field}`}
        className={cn(FIELD, problem !== null && 'border-red-500')}
        disabled={disabled}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {problem !== null && (
        <p className="text-[11px] text-red-500" data-identity-problem={field}>
          {identityProblemMessage(field, problem)}
        </p>
      )}
      {problem === null && value.trim() !== '' && (
        <p className="text-[11px] text-emerald-500 flex items-center gap-1" data-identity-ok={field}>
          <Check className="w-3 h-3" /> <T>platný</T>
        </p>
      )}
      {suggestion !== null && onAccept !== undefined && (
        <button
          type="button"
          data-accept-suggestion
          disabled={disabled}
          onClick={onAccept}
          className="text-[11px] text-primary underline disabled:opacity-50"
        >
          <T>Použiť</T> {suggestion} <T>(z DIČ)</T>
        </button>
      )}
    </Field>
  )
}

function Section({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        {icon}
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{title}</p>
      </div>
      <div className="divide-y divide-border">{children}</div>
    </div>
  )
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-6">
      <label className="text-sm font-semibold sm:w-56 shrink-0 pt-1.5" htmlFor={htmlFor}>
        {label}
      </label>
      <div className="flex-1 space-y-1.5 min-w-0">{children}</div>
    </div>
  )
}

function Toggle({
  label,
  description,
  value,
  disabled,
  onToggle,
  testId,
}: {
  label: string
  description: string
  value: boolean
  disabled: boolean
  onToggle: () => void
  testId: string
}) {
  return (
    <div className="px-5 py-4 flex items-center gap-4" data-toggle={testId}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        disabled={disabled}
        data-toggle-state={value ? 'on' : 'off'}
        onClick={onToggle}
        className={cn(
          'w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50',
          value ? 'bg-primary' : 'bg-muted',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 w-5 h-5 rounded-full bg-background transition-all',
            value ? 'left-[22px]' : 'left-0.5',
          )}
        />
      </button>
    </div>
  )
}
